import { useSnackbarContext } from "@ameelio/ui";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSound } from "use-sound";
import {
  Call,
  CallParticipant,
  CallType,
  Facility,
  ParticipantRole,
  ParticipantType,
  VirtualBackground,
} from "../../api/graphql";
import featureEnabled from "../../lib/featureEnabled.ts";
import retryImport from "../../lib/retryImport";
import { callIsWebinar } from "../../lib/securityFeaturesUtils.ts";
import useMayHearAudio from "../../lib/useMayHearAudio";
import { sendToParent } from "../iframeMessaging.ts";
import { useSegmenterContext } from "./SegmenterProvider.tsx";
import peerEnterSound from "./sounds/enterCall.mp3";
import peerExitSound from "./sounds/exitCall.mp3";
import SuperVideoCallInterface from "./SuperVideoCallInterface";
import { ConnectionInfo } from "./types/ConnectionInfo";
import CustomBackground from "./types/CustomBackground";
import VideoCallInterface from "./VideoCallInterface";
import VoiceCallInterface from "./VoiceCallInterface";

const ConnectCallConnection = React.lazy(() =>
  retryImport(() => import("./ConnectCallConnection"))
);

type Props = {
  currentParticipant: Pick<
    CallParticipant,
    "externalId" | "type" | "role" | "fullName"
  >;
  call: Pick<
    Call,
    | "id"
    | "url"
    | "token"
    | "features"
    | "startedAt"
    | "endedAt"
    | "callType"
    | "unlimited"
  > & {
    facility: Pick<Facility, "id" | "publicId" | "virtualBackgrounds">;
    participants: Pick<CallParticipant, "externalId" | "fullName" | "role">[];
  };
  onLeave: () => void;
  leaving: boolean;
  beginMuted: boolean;
  beginHidden: boolean;
  alerts: boolean;
  onToggleAlerts: () => void;
  onPeerJoined: () => void;
  onTerminated: () => void;
  onConnectedElsewhere: () => void;
  onAudioReady: () => void;
  onEnded: () => void;
};

export default function LiveCall({
  currentParticipant,
  call,
  beginMuted,
  beginHidden,
  onTerminated,
  onConnectedElsewhere,
  onAudioReady,
  onEnded,
  ...rest
}: Props) {
  const { t } = useTranslation();
  const snackbarContext = useSnackbarContext();
  const [playPeerEnter] = useSound(peerEnterSound, { volume: 0.5 });
  const [playPeerExit] = useSound(peerExitSound, { volume: 0.5 });

  const isWebinar = callIsWebinar(call);

  const isWebinarAttendee =
    currentParticipant.role === ParticipantRole.WebinarAttendee ||
    currentParticipant.role === ParticipantRole.WebinarIsolatedAttendee;

  // Force blur background for inmates if the facility requires it,
  // or they are webinar attendees.
  // TODO: This is just to replicate old webinar behavior.
  // We will have to decide a) whether to keep a webinar meeting type, or
  // b) gate this check on the capability SELF_CONTROL (but this would extend to all users that don't have SELF_CONTROL) or
  // c) create a new capability.
  const [customBackground, setCustomBackground] = useState<CustomBackground>(
    (call.facility.virtualBackgrounds === VirtualBackground.Required &&
      currentParticipant.type === ParticipantType.Inmate) ||
      isWebinarAttendee
      ? CustomBackground.BLUR
      : CustomBackground.NONE
  );

  const { status: segmenterStatus } = useSegmenterContext();

  // Shows a toast whenever the user cannot hear audio
  // due to missing the required headsets (see require_headsets option in connect-residents-app)
  const mayHearAudio = useMayHearAudio();
  useEffect(() => {
    if (!mayHearAudio) {
      snackbarContext.alert(
        "warning",
        t("Your audio is off because a headset is required and not connected.")
      );
    }
  }, [mayHearAudio, snackbarContext, t]);

  useEffect(() => {
    if (
      segmenterStatus === "failed" &&
      call.facility.virtualBackgrounds === VirtualBackground.Required
    ) {
      throw new Error(
        `Segmenter failed to load, but virtual backgrounds are required for this facility.`
      );
    }
  }, [call.facility.virtualBackgrounds, segmenterStatus]);

  const onMonitorJoined = () => {
    // Webinar hosts should be alerted with a toast
    // when a staff monitor joins
    if (isWebinar && currentParticipant.role === ParticipantRole.WebinarHost) {
      snackbarContext.alert(
        "info",
        t("A staff monitor joined and is observing your webinar.")
      );
      playPeerEnter();
    }
  };

  const onPeerConnected = (user: { id: string }) => {
    sendToParent("USER_JOINED", {
      callId: call.id,
      userId: user.id,
    });
    if (!isWebinar) playPeerEnter();
  };

  const onPeerDisconnected = () => {
    if (!isWebinar) playPeerExit();
  };

  const interval = { startAt: call.startedAt, endAt: call.endedAt };

  const useNewLayout =
    isWebinar || featureEnabled("VIDEO_MULTI_PARTICIPANT_LAYOUT");

  if (!call.url || !call.token) throw new Error(`invalid call data`);

  return (
    <ConnectCallConnection
      call={{
        url: call.url,
        token: call.token,
      }}
      callType={call.callType}
      isWebinar={isWebinar}
      muteAudio={beginMuted}
      muteVideo={beginHidden}
      voiceCall={call.callType === CallType.Call}
      currentUserId={currentParticipant.externalId}
      onMonitorJoined={onMonitorJoined}
      onPeerConnected={onPeerConnected}
      onPeerDisconnected={onPeerDisconnected}
      onTerminated={onTerminated}
      onEnded={onEnded}
      onConnectedElsewhere={onConnectedElsewhere}
      onAudioReady={onAudioReady}
      customBackground={customBackground}
    >
      {(connectionInfo: ConnectionInfo) =>
        call.callType === CallType.Call ? (
          <VoiceCallInterface
            features={call.features}
            participants={call.participants}
            connectionInfo={connectionInfo}
            currentParticipant={currentParticipant}
            unlimited={call.unlimited}
            interval={interval}
            {...rest}
          />
        ) : useNewLayout ? (
          <SuperVideoCallInterface
            features={call.features}
            facilityName={call.facility.publicId}
            connectionInfo={connectionInfo}
            currentParticipant={currentParticipant}
            participants={call.participants}
            interval={interval}
            beginMuted={beginMuted}
            beginHidden={beginHidden}
            canChooseBackground={
              currentParticipant.type !== ParticipantType.Inmate ||
              call.facility.virtualBackgrounds !== VirtualBackground.Unavailable
            }
            canRemoveBackground={
              (currentParticipant.type !== ParticipantType.Inmate ||
                call.facility.virtualBackgrounds ===
                  VirtualBackground.Optional) &&
              !isWebinarAttendee
            }
            customBackground={customBackground}
            onSetCustomBackground={(c: CustomBackground) =>
              setCustomBackground(c)
            }
            {...rest}
          />
        ) : (
          <VideoCallInterface
            features={call.features}
            facilityName={call.facility.publicId}
            connectionInfo={connectionInfo}
            currentParticipant={currentParticipant}
            participants={call.participants}
            interval={interval}
            beginMuted={beginMuted}
            beginHidden={beginHidden}
            canChooseBackground={
              currentParticipant.type !== ParticipantType.Inmate ||
              call.facility.virtualBackgrounds !== VirtualBackground.Unavailable
            }
            canRemoveBackground={
              currentParticipant.type !== ParticipantType.Inmate ||
              call.facility.virtualBackgrounds === VirtualBackground.Optional
            }
            customBackground={customBackground}
            onSetCustomBackground={(c: CustomBackground) =>
              setCustomBackground(c)
            }
            {...rest}
          />
        )
      }
    </ConnectCallConnection>
  );
}
